import React, { useRef, useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/auth';
import { api, type ModeloDrone } from '@/lib/api';
import JSZip from 'jszip';

const FORMATO_ICON: Record<string, string> = {
  b3dm: '🏗️', obj: '🧊', ply: '☁️', desconocido: '📦',
};
const FORMATO_LABEL: Record<string, string> = {
  b3dm: '3D Tiles (Cesium)', obj: 'Modelo OBJ', ply: 'Nube de puntos PLY', desconocido: 'Procesando…',
};

function formatBytes(bytes: string) {
  const n = Number(bytes);
  if (n < 1024) return `${n} B`;
  if (n < 1024 ** 2) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 ** 3) return `${(n / 1024 ** 2).toFixed(1)} MB`;
  return `${(n / 1024 ** 3).toFixed(2)} GB`;
}

const DroneIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#60a5fa" strokeWidth="1.6" strokeLinecap="round">
    <rect x="9.5" y="9.5" width="5" height="5" rx="1.2"/>
    <line x1="9.5" y1="9.5" x2="4.5" y2="4.5"/>
    <line x1="14.5" y1="9.5" x2="19.5" y2="4.5"/>
    <line x1="9.5" y1="14.5" x2="4.5" y2="19.5"/>
    <line x1="14.5" y1="14.5" x2="19.5" y2="19.5"/>
    <circle cx="4" cy="4" r="2.2"/><circle cx="20" cy="4" r="2.2"/>
    <circle cx="4" cy="20" r="2.2"/><circle cx="20" cy="20" r="2.2"/>
    <circle cx="12" cy="12" r="1.3" fill="#60a5fa" stroke="none"/>
  </svg>
);

function Tag({ children }: { children: React.ReactNode }) {
  return (
    <span style={{ fontSize: 9.5, background: 'rgba(255,255,255,0.07)', borderRadius: 5, padding: '2px 7px', color: 'rgba(255,255,255,0.45)' }}>
      {children}
    </span>
  );
}

export default function Plano() {
  const { token } = useAuthStore();
  const qc = useQueryClient();

  // Viewer
  const [viewerId, setViewerId] = useState<string | null>(null);

  // Upload state
  const fileRef = useRef<HTMLInputElement>(null);
  const folderRef = useRef<HTMLInputElement>(null);
  const [uploadMode, setUploadMode] = useState<'archivo' | 'carpeta'>('carpeta');
  const [dragging, setDragging] = useState(false);
  const [selectedInfo, setSelectedInfo] = useState<string>('');
  const [folderFiles, setFolderFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [zipping, setZipping] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ nombre: '', edificio: '', descripcion: '' });

  const { data: modelos = [], isLoading } = useQuery<ModeloDrone[]>({
    queryKey: ['modelos'],
    queryFn: () => api.modelos(token!),
    enabled: !!token,
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => api.eliminarModelo(token!, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['modelos'] }),
  });

  const viewerModelo = modelos.find(m => m.id === viewerId);

  // Manejo de archivo único
  const handleFile = useCallback((file: File) => {
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (!['zip', 'ply', 'obj', 'b3dm'].includes(ext ?? '')) {
      setError('Solo se permiten ZIP, PLY, OBJ o B3DM');
      return;
    }
    setError('');
    setSelectedInfo(`${file.name} (${formatBytes(String(file.size))})`);
    if (!form.nombre) setForm(f => ({ ...f, nombre: file.name.replace(/\.[^.]+$/, '') }));
    if (fileRef.current) {
      const dt = new DataTransfer();
      dt.items.add(file);
      fileRef.current.files = dt.files;
    }
  }, [form.nombre]);

  // Manejo de carpeta
  const handleFolder = useCallback((files: FileList) => {
    const arr = Array.from(files);
    if (arr.length === 0) return;
    setError('');
    setFolderFiles(arr);
    // Detectar nombre de la carpeta raíz
    const rootFolder = arr[0].webkitRelativePath.split('/')[0];
    setSelectedInfo(`📁 ${rootFolder}/ — ${arr.length} archivos`);
    if (!form.nombre) setForm(f => ({ ...f, nombre: rootFolder }));
  }, [form.nombre]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const items = e.dataTransfer.items;
    if (items && items[0]?.webkitGetAsEntry?.()?.isDirectory) {
      setError('Para carpetas usa el botón "Seleccionar carpeta" — el drag de carpetas no es compatible con todos los navegadores.');
      return;
    }
    if (e.dataTransfer.files[0]) handleFile(e.dataTransfer.files[0]);
  }, [handleFile]);

  async function handleUpload() {
    if (!form.nombre.trim()) { setError('El nombre del modelo es requerido'); return; }
    if (!form.edificio.trim()) { setError('El edificio/zona es requerido'); return; }

    let uploadFile: File | null = null;

    if (uploadMode === 'archivo') {
      uploadFile = fileRef.current?.files?.[0] ?? null;
      if (!uploadFile) { setError('Selecciona un archivo primero'); return; }
    } else {
      if (folderFiles.length === 0) { setError('Selecciona una carpeta primero'); return; }
      setZipping(true);
      setProgress(0);
      try {
        const zip = new JSZip();
        for (const f of folderFiles) {
          zip.file(f.webkitRelativePath, f);
        }
        const blob = await zip.generateAsync({ type: 'blob' }, (meta) => {
          setProgress(Math.round(meta.percent * 0.4)); // zip = primeros 40%
        });
        uploadFile = new File([blob], `${form.nombre.trim()}.zip`, { type: 'application/zip' });
      } catch {
        setZipping(false);
        setError('Error al comprimir la carpeta');
        return;
      }
      setZipping(false);
    }

    setError('');
    setUploading(true);

    const fd = new FormData();
    fd.append('archivo', uploadFile);
    fd.append('nombre', form.nombre.trim());
    fd.append('edificio', form.edificio.trim());
    if (form.descripcion) fd.append('descripcion', form.descripcion);

    const xhr = new XMLHttpRequest();
    xhr.open('POST', '/api/modelos');
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        const pct = Math.round((e.loaded / e.total) * 100);
        setProgress(uploadMode === 'carpeta' ? 40 + Math.round(pct * 0.6) : pct);
      }
    };

    xhr.onload = () => {
      setUploading(false);
      if (xhr.status === 201) {
        qc.invalidateQueries({ queryKey: ['modelos'] });
        setForm({ nombre: '', edificio: '', descripcion: '' });
        setSelectedInfo('');
        setFolderFiles([]);
        setProgress(0);
        if (fileRef.current) fileRef.current.value = '';
        if (folderRef.current) folderRef.current.value = '';
      } else {
        try { setError(JSON.parse(xhr.responseText).error ?? 'Error al subir'); }
        catch { setError('Error al subir el archivo'); }
      }
    };
    xhr.onerror = () => { setUploading(false); setError('Error de conexión'); };
    xhr.send(fd);
  }

  const busy = uploading || zipping;

  return (
    <div style={{ position: 'absolute', inset: 0, background: '#0a1628', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

      {/* Viewer overlay */}
      {viewerId && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 100, background: '#0a0a14', display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '10px 16px', background: 'rgba(10,22,40,0.95)', display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
            <button onClick={() => setViewerId(null)}
              style={{ background: 'rgba(255,255,255,0.08)', border: 'none', color: '#fff', borderRadius: 8, padding: '6px 14px', cursor: 'pointer', fontSize: 12 }}>
              ← Volver
            </button>
            <DroneIcon />
            <div>
              <div style={{ fontSize: 12, fontWeight: 800, color: '#fff' }}>{viewerModelo?.nombre}</div>
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)' }}>{viewerModelo?.edificio}</div>
            </div>
            <span style={{ fontSize: 10, background: 'rgba(96,165,250,0.15)', color: '#60a5fa', borderRadius: 6, padding: '3px 8px' }}>
              {FORMATO_LABEL[viewerModelo?.formato ?? 'desconocido']}
            </span>
          </div>
          <iframe src={`/api/modelos/${viewerId}/viewer?token=${token}`}
            style={{ flex: 1, border: 'none', background: '#0a0a14' }} title="Visor 3D" allow="fullscreen" />
        </div>
      )}

      {/* Contenido principal */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '82px 24px 32px' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
          <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(29,78,216,0.18)', border: '1px solid rgba(96,165,250,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <DroneIcon />
          </div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#fff' }}>Modelos 3D — Equipo Drone</div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)' }}>Sube exports de DJI Terra: carpetas ZIP, PLY o OBJ</div>
          </div>
          <div style={{ marginLeft: 'auto', fontSize: 11, color: 'rgba(255,255,255,0.3)' }}>{modelos.length} modelo{modelos.length !== 1 ? 's' : ''}</div>
        </div>

        {/* ── FORMULARIO DE SUBIDA ── */}
        <div style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 16, border: '1px solid rgba(255,255,255,0.08)', padding: '18px 20px', marginBottom: 24 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#60a5fa', letterSpacing: '0.07em', marginBottom: 14 }}>SUBIR NUEVO MODELO</div>

          {/* Tabs archivo / carpeta */}
          <div style={{ display: 'flex', gap: 4, marginBottom: 14, background: 'rgba(255,255,255,0.04)', borderRadius: 10, padding: 3 }}>
            {(['carpeta', 'archivo'] as const).map(m => (
              <button key={m} onClick={() => { setUploadMode(m); setSelectedInfo(''); setFolderFiles([]); setError(''); }}
                style={{ flex: 1, padding: '7px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 700, transition: 'all 0.15s',
                  background: uploadMode === m ? '#1d4ed8' : 'transparent',
                  color: uploadMode === m ? '#fff' : 'rgba(255,255,255,0.4)' }}>
                {m === 'carpeta' ? '📁 Carpeta (DJI Terra)' : '📄 Archivo único'}
              </button>
            ))}
          </div>

          {/* Zona de drop / selección */}
          {uploadMode === 'archivo' ? (
            <div
              onDragOver={e => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={handleDrop}
              onClick={() => fileRef.current?.click()}
              style={{ border: `2px dashed ${dragging ? '#60a5fa' : 'rgba(255,255,255,0.1)'}`, borderRadius: 12, padding: '24px 16px', textAlign: 'center', cursor: 'pointer', marginBottom: 14, transition: 'all 0.2s', background: dragging ? 'rgba(96,165,250,0.05)' : 'transparent' }}>
              <input ref={fileRef} type="file" accept=".zip,.ply,.obj,.b3dm" style={{ display: 'none' }} onChange={e => e.target.files?.[0] && handleFile(e.target.files[0])} />
              <div style={{ fontSize: 24, marginBottom: 6 }}>📂</div>
              <div style={{ fontSize: 12, fontWeight: 600, color: selectedInfo ? '#60a5fa' : 'rgba(255,255,255,0.5)' }}>
                {selectedInfo || 'Arrastra o haz clic — ZIP, PLY, OBJ, B3DM · Máx. 3 GB'}
              </div>
            </div>
          ) : (
            <div style={{ marginBottom: 14 }}>
              {/* Explicación carpeta */}
              <div style={{ background: 'rgba(96,165,250,0.07)', border: '1px solid rgba(96,165,250,0.15)', borderRadius: 10, padding: '10px 14px', marginBottom: 10, fontSize: 11, color: 'rgba(255,255,255,0.55)', lineHeight: 1.6 }}>
                <div style={{ fontWeight: 700, color: '#60a5fa', marginBottom: 4 }}>¿Cómo subir una carpeta de DJI Terra?</div>
                Selecciona la <b style={{ color: '#fff' }}>carpeta principal</b> del proyecto exportado. Puede contener subcarpetas (tiles, mesh, point cloud…). Se comprimirá automáticamente antes de subir.
              </div>
              <button onClick={() => folderRef.current?.click()}
                style={{ width: '100%', padding: '14px', borderRadius: 11, background: 'rgba(96,165,250,0.1)', border: '2px dashed rgba(96,165,250,0.3)', color: '#60a5fa', fontWeight: 700, fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                <span style={{ fontSize: 20 }}>📁</span>
                {selectedInfo || 'Seleccionar carpeta del proyecto'}
              </button>
              {/* Input invisible con webkitdirectory */}
              <input
                ref={folderRef}
                type="file"
                // @ts-ignore
                webkitdirectory=""
                multiple
                style={{ display: 'none' }}
                onChange={e => e.target.files && handleFolder(e.target.files)}
              />
              {folderFiles.length > 0 && (
                <div style={{ marginTop: 8, fontSize: 10, color: 'rgba(255,255,255,0.35)', lineHeight: 1.7 }}>
                  {folderFiles.slice(0, 5).map(f => (
                    <div key={f.webkitRelativePath}>↳ {f.webkitRelativePath}</div>
                  ))}
                  {folderFiles.length > 5 && <div style={{ color: 'rgba(255,255,255,0.2)' }}>…y {folderFiles.length - 5} archivos más</div>}
                </div>
              )}
            </div>
          )}

          {/* Campos */}
          <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
            <div style={{ flex: 1 }}>
              <div style={labelStyle}>Nombre del modelo *</div>
              <input value={form.nombre} onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))}
                placeholder="Ej: Bloque A Torre Norte" style={inputStyle} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={labelStyle}>Edificio / Zona *</div>
              <input value={form.edificio} onChange={e => setForm(f => ({ ...f, edificio: e.target.value }))}
                placeholder="Ej: Edificio Principal" style={inputStyle} />
            </div>
          </div>
          <div style={{ marginBottom: 14 }}>
            <div style={labelStyle}>Descripción</div>
            <input value={form.descripcion} onChange={e => setForm(f => ({ ...f, descripcion: e.target.value }))}
              placeholder="Fecha del vuelo, condiciones, observaciones…" style={inputStyle} />
          </div>

          {error && (
            <div style={{ fontSize: 11, color: '#ef4444', background: 'rgba(239,68,68,0.08)', borderRadius: 8, padding: '8px 12px', marginBottom: 12 }}>{error}</div>
          )}

          {busy && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>
                  {zipping ? 'Comprimiendo carpeta…' : 'Subiendo al servidor…'}
                </span>
                <span style={{ fontSize: 11, fontWeight: 700, color: '#60a5fa' }}>{progress}%</span>
              </div>
              <div style={{ height: 5, background: 'rgba(255,255,255,0.08)', borderRadius: 3 }}>
                <div style={{ height: '100%', background: 'linear-gradient(90deg,#1d4ed8,#60a5fa)', width: `${progress}%`, borderRadius: 3, transition: 'width 0.3s' }} />
              </div>
              {progress === 100 && (
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginTop: 4 }}>Procesando en el servidor…</div>
              )}
            </div>
          )}

          <button onClick={handleUpload} disabled={busy}
            style={{ width: '100%', padding: '11px', borderRadius: 11, background: busy ? 'rgba(29,78,216,0.3)' : '#1d4ed8', border: 'none', color: '#fff', fontWeight: 700, fontSize: 13, cursor: busy ? 'not-allowed' : 'pointer' }}>
            {zipping ? '📦 Comprimiendo…' : uploading ? '⬆ Subiendo…' : '⬆ Subir modelo'}
          </button>
        </div>

        {/* ── LISTA DE MODELOS ── */}
        <div style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.3)', letterSpacing: '0.08em', marginBottom: 12 }}>MODELOS CARGADOS</div>

        {isLoading && (
          <div style={{ textAlign: 'center', padding: 32 }}>
            <div style={{ width: 24, height: 24, borderRadius: '50%', border: '2.5px solid rgba(96,165,250,0.2)', borderTopColor: '#60a5fa', animation: 'spin 700ms linear infinite', margin: '0 auto 10px' }} />
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)' }}>Cargando modelos…</div>
          </div>
        )}

        {!isLoading && modelos.length === 0 && (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>🛸</div>
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.35)' }}>No hay modelos cargados aún</div>
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {modelos.map(m => (
            <div key={m.id} style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 14, border: '1px solid rgba(255,255,255,0.07)', padding: '13px 16px', display: 'flex', gap: 14, alignItems: 'center' }}>
              <div style={{ fontSize: 28, flexShrink: 0 }}>{FORMATO_ICON[m.formato]}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: '#fff' }}>{m.nombre}</div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginTop: 2 }}>📍 {m.edificio}</div>
                {m.descripcion && <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', marginTop: 2 }}>{m.descripcion}</div>}
                <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                  <Tag>{FORMATO_LABEL[m.formato]}</Tag>
                  <Tag>{formatBytes(m.fileSize)}</Tag>
                  <Tag>{new Date(m.createdAt).toLocaleDateString('es-VE', { day: '2-digit', month: 'short', year: 'numeric' })}</Tag>
                  {m.subidoPor && <Tag>👤 {m.subidoPor.nombre}</Tag>}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                {m.formato !== 'desconocido' && (
                  <button onClick={() => setViewerId(m.id)}
                    style={{ padding: '7px 14px', borderRadius: 9, background: '#1d4ed8', border: 'none', color: '#fff', fontSize: 11, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}>
                    ▶ Ver 3D
                  </button>
                )}
                <button onClick={() => { if (confirm(`¿Eliminar "${m.nombre}"?`)) deleteMut.mutate(m.id); }}
                  style={{ padding: '7px 10px', borderRadius: 9, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', color: '#ef4444', fontSize: 11, cursor: 'pointer' }}>
                  🗑
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 11px', borderRadius: 9,
  border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.05)',
  color: '#fff', fontSize: 12, outline: 'none', boxSizing: 'border-box',
};
const labelStyle: React.CSSProperties = {
  fontSize: 9.5, fontWeight: 700, color: 'rgba(255,255,255,0.35)',
  marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.06em',
};
